/**
 * Public Route Guard (simplified)
 * For / and /login:
 * - If user is authenticated, send them to the correct home once.
 * - Otherwise, just render the public page (no cookies/API auto-login).
 */

import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { storageManager } from '../../storage/storageManager';
import { STORAGE_KEYS } from '../../storage/storageKeys';

interface PublicRouteGuardProps {
  children: React.ReactNode;
  /** 'signup' for /, 'login' for /login */
  pageType: 'signup' | 'login';
}

export function PublicRouteGuard({ children, pageType }: PublicRouteGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) return;

    const checkAndRedirect = async () => {
      // If we have stored signup progress, always send user back into the signup flow
      // (including when they hit /signup directly or refresh on /signup) so step and category/subcategories are restored.
      const progress = await storageManager.get<{
        step?: number;
        source?: string;
        fullName?: string;
        email?: string;
        phoneNumber?: string;
        category?: string;
        subcategories?: string[];
      }>(STORAGE_KEYS.SIGNUP_PROGRESS);

      if (progress) {
        navigate('/signup', {
          replace: true,
          state: {
            startStep: progress.step && progress.step >= 4 && progress.step <= 6 ? progress.step : 4,
            source: (progress.source || 'normal') as string,
            fullName: progress.fullName,
            email: progress.email,
            phoneNumber: progress.phoneNumber,
            category: progress.category,
            subcategories: progress.subcategories,
          },
        });
        return;
      }

      const role = (user?.role ?? '').toString().toLowerCase();
      const isAgency = role === 'agency' || role === 'agency_owner';
      const target = isAgency ? '/agency-home' : '/user-home';

      // For login page: always redirect authenticated users away to their home
      if (pageType === 'login') {
        if (location.pathname !== target) {
          navigate(target, { replace: true });
        }
        return;
      }

      // For signup pages:
      // - Allow authenticated users with incomplete onboarding to stay on `/signup`
      //   so they can finish the multi-step flow.
      // - Still redirect from the root marketing/landing route `/` once authenticated.
      if (pageType === 'signup' && location.pathname === '/') {
        navigate(target, { replace: true });
      }
    };

    void checkAndRedirect();
  }, [isAuthenticated, user?.role, location.pathname, navigate, pageType]);

  return <>{children}</>;
}
